import Head from "next/head";
import { getWsrvUrl } from "@/utils/wsrv";

const MetaTags = ({
  title,
  keywords,
  description,
  image,
  robots,
  maxSnippet,
  url,
}) => {
  const robotsContent = [
    robots,
    maxSnippet ? `max-snippet:${maxSnippet}` : null,
  ]
    .filter(Boolean)
    .join(", ");

  const websiteUrl = process.env.NEXT_PUBLIC_SITE_URL;
  const siteTitle = "CLC - Career Line Coaching, Sikar";
  const siteDescription =
    "India's Most Reliable Test Series for NEET, IIT-JEE. Practice Like a Topper, Perform Like a Champion.";
  const siteImageRaw =
    "https://i.filecdn.in/807clcworld/clc-logo-new-1674119982632.png";

  const finalTitle = title || siteTitle;
  const finalDescription = description || siteDescription;
  const finalImageRaw = image || siteImageRaw;
  // Use local copy for og:image to avoid third-party cookies
  const finalImage = image ? getWsrvUrl(image, { w: 1200, q: 80, output: "webp" }) : "/clc-logo.png";

  const websiteUrlClean = websiteUrl?.replace(/\/$/, "") || "https://lms.clclive.in";
  const canonicalUrl = url ? `${websiteUrlClean}${url.startsWith("/") ? url : `/${url}`}` : websiteUrlClean;

  // Structured Data for Agentic Browsing / SEO
  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    name: siteTitle,
    url: websiteUrlClean,
    logo: siteImageRaw,
    description: siteDescription,
    sameAs: [],
  };

  const websiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: siteTitle,
    url: websiteUrlClean,
    description: siteDescription,
    potentialAction: {
      "@type": "SearchAction",
      target: `${websiteUrlClean}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  const breadcrumbJsonLd = url
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: websiteUrlClean,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: finalTitle,
            item: canonicalUrl,
          },
        ],
      }
    : null;

  return (
    <Head>
      <title>{finalTitle}</title>
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:title" content={finalTitle} />
      <meta name="twitter:title" content={finalTitle} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="description" content={finalDescription} />
      <meta property="og:description" content={finalDescription} />
      <meta name="twitter:description" content={finalDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:image" content={finalImage} />
      <link rel="canonical" href={canonicalUrl} />
      {/* AI / Agentic hints */}
      <link rel="alternate" type="text/plain" href="/llms.txt" title="LLM-friendly site index" />
      <meta name="llms-txt" content="/llms.txt" />
      {keywords && <meta name="keywords" content={keywords} />}
      {robotsContent && <meta name="robots" content={robotsContent} />}
      {/* Preload LCP image if available */}
      {finalImageRaw && finalImageRaw.startsWith("http") && (
        <link
          rel="preload"
          as="image"
          href={getWsrvUrl(finalImageRaw, { w: 1200, q: 80, output: "webp" })}
          imageSrcSet={`${getWsrvUrl(finalImageRaw, { w: 640, q: 80 })} 640w, ${getWsrvUrl(
            finalImageRaw,
            { w: 1200, q: 80 }
          )} 1200w`}
          imageSizes="100vw"
        />
      )}
      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteJsonLd) }} />
      {breadcrumbJsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      )}
    </Head>
  );
};

export default MetaTags;
